import {
  UserOutlined,
  ApartmentOutlined,
  DownSquareOutlined,
  CalendarOutlined,
  ProfileOutlined,
  CreditCardOutlined,
  CommentOutlined,
  ShoppingOutlined,
  TableOutlined,
  BarcodeOutlined,
  CameraOutlined,
  CalculatorOutlined,
  TeamOutlined,
  TagsOutlined,
  FundProjectionScreenOutlined,
  AccountBookOutlined,
  SlidersOutlined,
  CloudServerOutlined,
  ApiOutlined,
  TransactionOutlined,
  AppstoreOutlined,
  FacebookOutlined,
  FileTextOutlined,
  SettingOutlined,
  DeliveredProcedureOutlined,
  IdcardOutlined,
  PlayCircleOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { ProductCategory } from 'models/store';
import { Role, User, UserPermissions } from 'models/user';
import { AiOutlineGlobal } from 'react-icons/ai';
import { LuActivity, LuWorkflow } from 'react-icons/lu';
import { PiKanbanLight } from 'react-icons/pi';
import { TbTarget } from 'react-icons/tb';

export type ProvidedPathVar = 'TEAM_ID' | 'USER_ID';

export interface SidebarItem {
  key: React.Key;
  label: string;
  icon: React.ElementType;
  availableFor: Role[];
  // Для ADMIN requiredPermissions игнорируется
  requiredPermissions?: (keyof UserPermissions | Partial<Record<Role, keyof UserPermissions>>)[];
  path: string;
  providedPathVar?: ProvidedPathVar;
  children?: SidebarItem[];
}

export const getSidebarData = (user: User, children?: ProductCategory[]): SidebarItem[] => [
  {
    key: '0',
    label: 'Основное меню',
    icon: AppstoreOutlined,
    availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER', 'HR'],
    path: '/',
    children: [
      {
        key: '0-0',
        label: 'Мои данные',
        icon: UserOutlined,
        path: '/me',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER', 'HR'],
      },
      {
        key: '0-1',
        label: 'Пользователи',
        icon: TeamOutlined,
        path: '/users',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BIZDEV', 'MANAGER'],
        requiredPermissions: [{ BIZDEV: 'viewUsersPage', MANAGER: 'viewUsersPage' }],
      },
      {
        key: '0-2',
        label: 'Звена',
        icon: ApartmentOutlined,
        path: '/teams',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD'],
      },
      {
        key: '0-3',
        label: 'Мое звено',
        icon: ApartmentOutlined,
        path: '/teams/',
        availableFor: [],
        providedPathVar: 'TEAM_ID',
      },
      {
        key: 'task-manager',
        label: 'Менеджер задач',
        icon: DownSquareOutlined,
        path: '/tasks',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
      },
      {
        key: 'tasks-board',
        label: 'Доска задач',
        icon: AppstoreOutlined,
        path: '/tasks-board/main',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV'],
        requiredPermissions: ['viewTasksBoardPage'],
        ...(user.role === 'ADMIN' || (user.role === 'BIZDEV' && user.permissions?.viewTasksBoardStatsPage)
          ? {
              children: [
                {
                  key: 'tasks-board/main',
                  label: 'Главная',
                  icon: PiKanbanLight,
                  path: '/tasks-board/main',
                  availableFor: ['ADMIN', 'BIZDEV'],
                },
                {
                  key: 'tasks-board/stats',
                  label: 'Статистика',
                  icon: BarChartOutlined,
                  path: '/tasks-board/stats',
                  requiredPermissions: ['viewTasksBoardStatsPage'],
                  availableFor: ['ADMIN', 'BIZDEV'],
                },
              ],
            }
          : {}),
      },
      {
        key: '0-4',
        label: 'Календарь',
        icon: CalendarOutlined,
        path: '/calendar',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD'],
      },
      {
        key: '0-5',
        label: 'Мой календарь',
        icon: CalendarOutlined,
        path: '/calendar/',
        availableFor: ['TEAM_LEAD', 'BUYER'],
        providedPathVar: 'TEAM_ID',
      },
      {
        key: '0-6',
        label: 'Партнерки',
        icon: FundProjectionScreenOutlined,
        path: '/affiliates',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
        children: [
          {
            key: '0-6-1',
            label: 'Офферы',
            icon: TagsOutlined,
            path: 'affiliates/offers',
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
          },
          {
            key: '0-6-2',
            label: 'W/B персонажи',
            icon: TeamOutlined,
            path: 'affiliates/persons',
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
          },
          {
            key: '0-6-3',
            label: 'Калькулятор',
            icon: CalculatorOutlined,
            path: 'affiliates/calculator',
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
          },
          {
            key: '0-6-4',
            label: 'Управление',
            icon: SlidersOutlined,
            path: 'affiliates/manage',
            availableFor: ['ADMIN', 'BIZDEV'],
            requiredPermissions: ['canManageAffiliates'],
          },
        ],
      },
      {
        key: '0-7',
        label: 'Магазин',
        icon: CreditCardOutlined,
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
        path: '/store',
        children: [
          {
            key: '0-7-1',
            label: 'Витрина',
            icon: TableOutlined,
            path: '/store/showcase',
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
          },
          {
            key: '0-7-2',
            label: 'Заказы',
            icon: ShoppingOutlined,
            path: '/store/orders',
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
          },
          {
            key: '0-7-3',
            label: 'Мои товары',
            icon: BarcodeOutlined,
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
            path: '/store/products',
            children: children ? formatProductsChildren(children) : [],
          },
        ],
      },
      {
        key: '0-8',
        label: 'Галерея',
        icon: CameraOutlined,
        path: '/gallery',
        availableFor: ['ADMIN'],
      },
      {
        key: '0-9',
        label: 'Моя галерея',
        icon: CameraOutlined,
        path: '/gallery/',
        availableFor: ['PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
        providedPathVar: 'USER_ID',
      },
      {
        key: 'videos',
        label: 'Видео',
        icon: PlayCircleOutlined,
        path: '/videos',
        availableFor: [
          'ADMIN',
          'PARTNER',
          'HEAD',
          'TEAM_LEAD',
          'BUYER',
          'BIZDEV',
          'MANAGER',
          'FARMER',
          'HR',
          'NEW_USER',
        ],
        requiredPermissions: ['viewVideosPage'],
      },
      {
        key: 'domains',
        label: user.role === 'ADMIN' ? 'Домены' : 'Мои домены',
        icon: CloudServerOutlined,
        path: '/domains',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
        // подменю нужно только админу — у остальных доступен только раздел «Мои домены»
        ...(user.role === 'ADMIN'
          ? {
              children: [
                {
                  key: 'domains-my',
                  label: 'Мои домены',
                  icon: CloudServerOutlined,
                  path: '/domains',
                  availableFor: ['ADMIN'],
                },
                {
                  key: 'domains-manage',
                  label: 'Управление доменами',
                  icon: SlidersOutlined,
                  path: '/domains/manage',
                  availableFor: ['ADMIN'],
                },
                {
                  key: 'domains-vias',
                  label: 'Связки',
                  icon: ApiOutlined,
                  path: '/domains/vias',
                  availableFor: ['ADMIN'],
                },
              ],
            }
          : {}),
      },
      {
        key: '0-11',
        label: 'Расходы',
        icon: AccountBookOutlined,
        path: '/costs',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
      },
      {
        key: '0-12',
        label: 'Лиды',
        icon: TransactionOutlined,
        path: '/leads',
        availableFor: ['ADMIN', 'BIZDEV', 'MANAGER'],
      },
      {
        key: '0-13',
        label: 'Кандидаты',
        icon: IdcardOutlined,
        path: '/candidates',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER', 'HR'],
        requiredPermissions: [
          {
            PARTNER: 'viewCandidatesPage',
            HEAD: 'viewCandidatesPage',
            TEAM_LEAD: 'viewCandidatesPage',
            BUYER: 'viewCandidatesPage',
            BIZDEV: 'viewCandidatesPage',
            MANAGER: 'viewCandidatesPage',
          },
        ],
      },
      {
        key: 'capi',
        label: 'CAPI',
        icon: AiOutlineGlobal,
        path: '/capi',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
        requiredPermissions: ['viewCapiPage'],
        children: [
          {
            key: 'capi_flows',
            label: 'Постбеки',
            icon: LuWorkflow,
            path: '/capi/flows',
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
          },
          {
            key: 'capi_events',
            label: 'События',
            icon: LuActivity,
            path: '/capi/events',
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
          },
          {
            key: 'capi_pixels',
            label: 'Пиксели',
            icon: TbTarget,
            path: '/capi/pixels',
            availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
          },
        ],
      },
    ],
  },
  {
    key: '1',
    label: 'FB manager',
    icon: FacebookOutlined,
    availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
    path: '/fbmanager',
    children: [
      {
        key: '1-1',
        label: 'Главная',
        icon: FacebookOutlined,
        path: '/fbmanager/main',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
      },
      {
        key: '1-2',
        label: 'Комментарии',
        icon: CommentOutlined,
        path: '/fbmanager/comments',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
      },
      {
        key: '1-3',
        label: 'Автоправила',
        icon: FileTextOutlined,
        path: '/fbmanager/cabs/rules',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
      },
      {
        key: '1-4',
        label: 'Прокси',
        icon: DeliveredProcedureOutlined,
        path: '/fbmanager/proxy',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
      },
      {
        key: '1-5',
        label: 'Настройки',
        icon: SettingOutlined,
        path: '/fbmanager/settings',
        availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER', 'BIZDEV', 'MANAGER'],
      },
    ],
  },
];

export const formatProductsChildren = (children: ProductCategory[]): SidebarItem[] =>
  children.map((child) => ({
    key: 'product' + child.id,
    label: child.name,
    icon: ProfileOutlined,
    path: `/store/products/${child.id}`,
    availableFor: ['ADMIN', 'PARTNER', 'HEAD', 'TEAM_LEAD', 'BUYER'],
  }));
